import { API_ROUTES } from '@config/routes';
import api from './api';
import type {
  User,
  ProfileUpdateFormData,
  PasswordUpdateFormData,
  UserClientPortalSettings,
} from '@/types';

export interface PublicClientPortalResponse {
  businessName: string;
  clientPortalSettings: UserClientPortalSettings;
}

export interface UserBackupRestorePoint {
  id: string;
  fileName: string | null;
  exportedAt: string | null;
  createdAt: string | null;
  sizeBytes: number;
  source: string;
}

export interface UserBackupRestoreResponse {
  backups: UserBackupRestorePoint[];
  preRestoreBackupId?: string;
  restoredAt?: string;
  backupId?: string;
}

export interface UserBackupExportResponse {
  blob: Blob;
  backups: UserBackupRestorePoint[];
}

export const userService = {
  // Get user profile
  getProfile: async (): Promise<User> => api.get(API_ROUTES.USERS.PROFILE),

  // Update user profile
  updateProfile: async (data: ProfileUpdateFormData): Promise<User> =>
    api.put(API_ROUTES.USERS.PROFILE, data),

  getPublicClientPortal: async (shareToken: string): Promise<PublicClientPortalResponse> =>
    api.get(API_ROUTES.USERS.CLIENT_PORTAL_PUBLIC(shareToken), { raw: false }),

  // Update password
  updatePassword: async (data: PasswordUpdateFormData): Promise<void> =>
    api.put(API_ROUTES.USERS.UPDATE_PASSWORD, data),

  // Upload avatar
  uploadAvatar: async (file: File): Promise<{ photoURL: string }> => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post(API_ROUTES.USERS.UPDATE_AVATAR, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Delete avatar
  deleteAvatar: async (): Promise<void> => api.delete(API_ROUTES.USERS.UPDATE_AVATAR),

  // Delete account
  deleteAccount: async (): Promise<void> => api.delete('/users/account'),

  listBackups: async (): Promise<UserBackupRestorePoint[]> => api.get(API_ROUTES.USERS.BACKUPS),

  exportBackup: async (): Promise<UserBackupExportResponse> => {
    const response = await api.getWithHeaders<Blob>(API_ROUTES.USERS.BACKUPS_EXPORT, {
      raw: true,
      responseType: 'blob',
    } as never);
    const headerValue = response.headers['x-drahft-backups'];
    let headerString = '';
    if (typeof headerValue === 'string') {
      headerString = headerValue;
    } else if (Array.isArray(headerValue) && typeof headerValue[0] === 'string') {
      const [firstHeaderValue] = headerValue;
      headerString = firstHeaderValue;
    }

    let backups: UserBackupRestorePoint[] = [];
    if (headerString) {
      try {
        const parsed = JSON.parse(headerString) as unknown;
        if (Array.isArray(parsed)) {
          backups = parsed as UserBackupRestorePoint[];
        }
      } catch (error) {
        console.warn('[userService] Failed to parse backup metadata header:', error);
      }
    }

    return { blob: response.data, backups };
  },

  importBackupData: async (backupData: unknown): Promise<UserBackupRestoreResponse> =>
    api.post(API_ROUTES.USERS.BACKUPS_IMPORT, { backupData }),

  restoreBackupPoint: async (backupId: string): Promise<UserBackupRestoreResponse> =>
    api.post(API_ROUTES.USERS.BACKUPS_IMPORT, { backupId }),
};
